import type { HTMLAttributes, ReactNode } from 'react';

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  label: ReactNode;
  tone?: BadgeTone;
}

const css = `
  .ew-badge {
    font: var(--ew-fw-medium) var(--ew-fs-caption)/1 var(--ew-font);
    padding: 3px 8px;
    border-radius: var(--ew-r-pill);
    white-space: nowrap;
    display: inline-block;
  }
  .ew-badge--neutral { background: var(--ew-n-100); color: var(--ew-n-700); }
  .ew-badge--success { background: var(--ew-green-50); color: var(--ew-green-700); }
  .ew-badge--warning { background: #FEF3C7;            color: #92400E;           }
  .ew-badge--danger  { background: #FBE4DF;            color: #8F2414;           }
  .ew-badge--info    { background: #E1EEF7;            color: #0D4C7C;           }
`;

/**
 * Pill-shaped status badge. Five tones cover every existing one-off pill
 * in the app (StatusBadge, MobStatusBadge, ProjectStatusBadge, RENUMBER,
 * "escalated", review-type chips, etc.).
 *
 * Tone color map:
 *   neutral → `--ew-n-100` / `--ew-n-700`
 *   success → `--ew-green-50` / `--ew-green-700`
 *   warning → safety-yellow tint
 *   danger  → `--ew-danger` tint
 *   info    → `--ew-quality` (info) tint
 *
 * @example
 *   <Badge label="Awaiting Financials" tone="warning" />
 *   <Badge label="Complete" tone="success" />
 */
export default function Badge({
  label,
  tone = 'neutral',
  className,
  ...rest
}: BadgeProps) {
  const classes = ['ew-badge', `ew-badge--${tone}`, className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <>
      <style>{css}</style>
      <span className={classes} {...rest}>
        {label}
      </span>
    </>
  );
}
